import "./GanttMarkerCluster.css";

import { useEffect, useRef, useState } from "react";
import { JSX } from "react/jsx-runtime";

import { useClickOutside } from "../../../../../utils/hooks/useClickOutside";
import { BADGE_SIZE, DIAMOND_SIZE, LINE_WIDTH, PHASE_BAR_HEIGHT } from "../../constants";
import { GanttMarkerClusterProps, GanttTooltipData } from "../../models";

export const GanttMarkerCluster = ({
  x,
  rowHeight,
  shape,
  color,
  items,
  noun,
  markerHeight: lineHeight,
  onShowTooltip,
  onHideTooltip,
}: GanttMarkerClusterProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click (shared hook, only while open) and on Escape.
  useClickOutside(ref, () => setOpen(false), open);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const count = items.length;
  const summary = `${count} ${noun}`;

  const tooltip: GanttTooltipData = {
    title: summary,
    accentColor: color,
    meta: items.slice(0, 6).map((it) => ({ label: it.label, value: it.detail })),
  };

  const markerHeight = shape === "diamond" ? DIAMOND_SIZE : (lineHeight ?? PHASE_BAR_HEIGHT);
  const markerTop = (rowHeight - markerHeight) / 2;
  const badgeTop = Math.max(0, markerTop - BADGE_SIZE / 2);
  const markerStyle =
    shape === "diamond"
      ? {
          left: -DIAMOND_SIZE / 2,
          top: markerTop,
          width: DIAMOND_SIZE,
          height: DIAMOND_SIZE,
          backgroundColor: color,
          borderColor: color,
        }
      : { left: -LINE_WIDTH / 2, top: markerTop, width: LINE_WIDTH, height: markerHeight, backgroundColor: color };

  return (
    <div
      className={`GanttClusterAnchor${open ? " GanttClusterAnchor_open" : ""}`}
      ref={ref}
      style={{ left: x, height: rowHeight }}
    >
      <button
        type="button"
        className={`GanttClusterMarker GanttClusterMarker_${shape}`}
        style={markerStyle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${summary} — click to expand`}
        onClick={(e) => {
          e.stopPropagation();
          onHideTooltip?.();
          setOpen((o) => !o);
        }}
        onMouseEnter={(e) => {
          if (!open) onShowTooltip?.(tooltip, e);
        }}
        onMouseLeave={onHideTooltip}
      />
      <span className="GanttClusterCount body5" aria-hidden="true" style={{ top: badgeTop }}>
        {count}
      </span>
      {open && (
        <div className="GanttClusterPopover" role="dialog" aria-label={summary}>
          <p className="GanttClusterPopoverTitle body4">{summary}</p>
          <ul className="GanttClusterPopoverList">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  className="GanttClusterPopoverItem body2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    it.onSelect();
                  }}
                >
                  <span
                    className={`GanttClusterPopoverDot GanttClusterPopoverDot_${shape}`}
                    style={{ backgroundColor: it.color }}
                    aria-hidden="true"
                  />
                  <span className="GanttClusterPopoverItemLabel">{it.label}</span>
                  <span className="GanttClusterPopoverItemDetail">{it.detail}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
